/**
 * @license
 * Copyright 2024 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
import { GraphDescriberManager } from "./graph-describer-manager.js";
import { GraphQueries } from "./graph-queries.js";
import { ok } from "../../data/file-system/utils.js";
export { Graph };
class Graph {
    #graphId;
    #mutable;
    constructor(graphId, mutableGraph) {
        this.#graphId = graphId;
        this.#mutable = mutableGraph;
    }
    #descriptor() {
        const graph = this.#mutable.graph;
        return this.#graphId ? graph.graphs[this.#graphId] : graph;
    }
    raw() {
        return this.#descriptor();
    }
    mainGraphDescriptor() {
        return this.#mutable.graph;
    }
    imperative() {
        return !!this.main();
    }
    main() {
        return this.#descriptor().main;
    }
    metadata() {
        return this.#descriptor().metadata;
    }
    nodesByType(type) {
        return this.#mutable.nodes.byType(type, this.#graphId);
    }
    nodeById(id) {
        return new GraphQueries(this.#mutable, this.#graphId).nodeById(id);
    }
    nodes() {
        return this.#mutable.nodes.nodes(this.#graphId);
    }
    moduleById(id) {
        return this.#mutable.modules.get(id);
    }
    modules() {
        return this.#mutable.modules.modules();
    }
    edges() {
        return this.#mutable.edges.edges(this.#graphId);
    }
    hasEdge(edge) {
        return this.#mutable.edges.hasByValue(edge, this.#graphId);
    }
    typeForNode(id) {
        return new GraphQueries(this.#mutable, this.#graphId).typeForNode(id);
    }
    typeById(id) {
        return new GraphQueries(this.#mutable, this.#graphId).typeById(id);
    }
    incomingForNode(id) {
        return new GraphQueries(this.#mutable, this.#graphId).incoming(id);
    }
    outgoingForNode(id) {
        return new GraphQueries(this.#mutable, this.#graphId).outgoing(id);
    }
    entries() {
        return new GraphQueries(this.#mutable, this.#graphId).entries();
    }
    async describe(inputs, context) {
        const manager = GraphDescriberManager.create(this.#graphId, this.#mutable);
        if (!ok(manager)) {
            throw new Error(`Inspect API Integrity Error: ${manager.$error}`);
        }
        return manager.describe(inputs, undefined, undefined, context);
    }
    graphs() {
        if (this.#graphId)
            return;
        return this.#mutable.graphs.graphs();
    }
    graphId() {
        return this.#graphId;
    }
    moduleExports() {
        return new GraphQueries(this.#mutable, this.#graphId).moduleExports();
    }
    graphExports() {
        return new GraphQueries(this.#mutable, this.#graphId).graphExports();
    }
    imports() {
        return new GraphQueries(this.#mutable, this.#graphId).imports();
    }
    assets() {
        return new GraphQueries(this.#mutable, this.#graphId).assets();
    }
    assetEdges() {
        return new GraphQueries(this.#mutable, this.#graphId).assetEdges();
    }
}
//# sourceMappingURL=graph.js.map